import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  type ButtonProps,
  DialogActions,
  type DialogActionsProps,
  DialogContent,
  type DialogContentProps,
  type DialogProps,
  DialogTitle,
  type DialogTitleProps,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Dialog } from "@surfaces/Dialog";
import { type ReactNode, useState } from "react";

export interface IModalConfirmationOptions {
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
}

export interface IModalConfirmationSlotProps {
  dialog?: Omit<DialogProps, "open">;
  dialogTitle?: DialogTitleProps;
  dialogContent?: DialogContentProps;
  dialogActions?: DialogActionsProps;
  confirmButton?: ButtonProps;
  cancelButton?: ButtonProps;
}

export interface IModalConfirmationProps {
  open: boolean;
  resolve: (result: boolean) => void;
  title: ReactNode;
  message: ReactNode;
  options?: IModalConfirmationOptions;
  slotProps?: IModalConfirmationSlotProps;
}

export const ModalConfirmation = ({ open, resolve, title, message, options, slotProps }: IModalConfirmationProps) => {
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const handleCancel = async () => {
    if (options?.onCancel) {
      const result = options.onCancel();
      if (typeof result === "object" && result !== null && typeof result.then === "function") {
        setCancelLoading(true);
        await result;
        setCancelLoading(false);
      }
    }
    resolve(false);
  };

  const handleConfirm = async () => {
    if (options?.onConfirm) {
      const result = options.onConfirm();
      if (typeof result === "object" && result !== null && typeof result.then === "function") {
        setConfirmLoading(true);
        try {
          await result;
          resolve(true);
        } catch (error) {
          console.error(error);
        }
        setConfirmLoading(false);
      } else {
        resolve(true);
      }
    } else {
      resolve(true);
    }
  };

  return (
    <Dialog {...slotProps?.dialog} open={open}>
      <DialogTitle {...slotProps?.dialogTitle}>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h4">{title}</Typography>
          <IconButton onClick={handleCancel} sx={{ mr: -1 }}>
            <CloseIcon sx={(theme) => ({ color: theme.palette.black.light })} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent {...slotProps?.dialogContent}>{message}</DialogContent>
      <DialogActions {...slotProps?.dialogActions}>
        <Button
          variant="outlined"
          color="primary"
          {...slotProps?.cancelButton}
          onClick={handleCancel}
          disabled={cancelLoading || confirmLoading}
          loading={cancelLoading}
        >
          {options?.cancelLabel ?? "Annuler"}
        </Button>
        <Button
          variant="contained"
          color="primary"
          {...slotProps?.confirmButton}
          onClick={handleConfirm}
          disabled={cancelLoading || confirmLoading}
          loading={confirmLoading}
        >
          {options?.confirmLabel ?? "Confirmer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
